import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import { ApiLoadingState } from '../../../middleware/api/apiList'
import { LoadingState, ValidationError } from '../../../types/commonTypes'
import {
  GetInvoiceSummaryResponse,
  GetProjects,
  GetSentInvoices,
  InvoiceSummaryProps,
  inVoiceSummaryState,
} from '../../../types/Invoicing/InvoiceSummary/invoiceSummaryType'
import invoiceSummaryApi from '../../../middleware/api/Invoicing/InvoiceSummary/invoiceSummaryApi'
import { RootState } from '../../../stateStore'

const getProjects = createAsyncThunk(
  'invoiceSummary/getProjects',
  async (_, thunkApi) => {
    try {
      return await invoiceSummaryApi.getProjects()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
const getInvoiceSummary = createAsyncThunk(
  'invoiceSummary/getInvoiceSummary',
  async (props: InvoiceSummaryProps, thunkApi) => {
    try {
      return await invoiceSummaryApi.getInvoiceSummary(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
const getSentInvoices = createAsyncThunk(
  'invoiceSummary/getSentInvoices',
  async (summaryId: number, thunkApi) => {
    try {
      return await invoiceSummaryApi.getSentInvoices(summaryId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

export const initialInvoiceInvoiceSummaryState: inVoiceSummaryState = {
  getProjects: [],
  getInvoiceSummaryResponse: {} as GetInvoiceSummaryResponse,
  isLoading: ApiLoadingState.idle,
  getSentInvoices: [],
}
const invoiceSummarySlice = createSlice({
  name: 'invoiceSummary',
  initialState: initialInvoiceInvoiceSummaryState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getProjects.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.getProjects = action.payload
      })
      .addCase(getInvoiceSummary.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.getInvoiceSummaryResponse = action.payload
      })
      .addCase(getSentInvoices.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.getSentInvoices = action.payload
      })
      .addMatcher(
        isAnyOf(
          getProjects.pending,
          getInvoiceSummary.pending,
          getSentInvoices.pending,
        ),
        (state) => {
          state.isLoading = ApiLoadingState.loading
        },
      )
  },
})
const invoiceSummaryThunk = {
  getProjects,
  getInvoiceSummary,
  getSentInvoices,
}
const isLoading = (state: RootState): LoadingState =>
  state.invoiceSummary.isLoading

const getprojectsData = (state: RootState): GetProjects[] =>
  state.invoiceSummary.getProjects

const getInVoiceSummary = (state: RootState): GetInvoiceSummaryResponse =>
  state.invoiceSummary.getInvoiceSummaryResponse

const getSentInvoicesSummary = (state: RootState): GetSentInvoices[] =>
  state.invoiceSummary.getSentInvoices

const invoiceSummarySelectors = {
  isLoading,
  getprojectsData,
  getInVoiceSummary,
  getSentInvoicesSummary,
}
export const invoiceSummaryService = {
  ...invoiceSummaryThunk,
  actions: invoiceSummarySlice.actions,
  selectors: invoiceSummarySelectors,
}
export default invoiceSummarySlice.reducer
